import { useState } from "react";

import { linkForColumn } from "../api/links";
import { Guid, Panel, useJsonDisclosure } from "./primitives";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function Columns({ row, omit = [], self }: { row: object; omit?: string[]; self?: string }) {
  const [filter, setFilter] = useState("");
  const record = row as Record<string, unknown>;
  const entries = Object.entries(record).filter(
    ([key]) => !omit.includes(key) && key.includes(filter.trim().toLowerCase()),
  );

  return (
    <>
      <div className="border-b border-line px-4 py-2">
        <input
          className="field-input py-1 text-xs"
          placeholder="filter columns…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          spellCheck={false}
        />
      </div>
      <dl
        className="grid gap-x-8 px-4 py-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 560px), 1fr))" }}
      >
        {entries.map(([key, value]) => (
          <div
            key={key}
            className="grid grid-cols-[220px_1fr] items-baseline gap-x-3 border-b border-line/50 py-1"
          >
            <dt className="mono truncate text-muted" title={key}>
              {key}
            </dt>
            <dd className="min-w-0 overflow-x-auto">
              <Value column={key} value={value} row={record} linked={key !== self} />
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}

export function ColumnsPanel({
  table,
  id,
  row,
  omit = [],
  self,
}: {
  table: string;
  id: string;
  row: object;
  omit?: string[];
  self?: string;
}) {
  const count = Object.keys(row).length - omit.length;
  return (
    <Panel id={id} title={`${table} · ${count} columns`}>
      <Columns row={row} omit={omit} self={self} />
    </Panel>
  );
}

function Value({
  column,
  value,
  row,
  linked,
}: {
  column: string;
  value: unknown;
  row: Record<string, unknown>;
  linked: boolean;
}) {
  if (value === null || value === undefined) return <span className="mono text-faint">null</span>;
  if (typeof value === "object") return <JsonValue value={value} />;
  if (typeof value === "string" && GUID.test(value)) {
    return <Guid value={value} to={linked ? linkForColumn(column, value, row) : undefined} />;
  }
  if (value === "") return <span className="mono text-faint">""</span>;
  return <span className="mono whitespace-pre-wrap break-words text-body">{String(value)}</span>;
}

function JsonValue({ value }: { value: unknown }) {
  const { toggle, panel } = useJsonDisclosure(value);
  return (
    <>
      {toggle}
      {panel}
    </>
  );
}
